import React from 'react';
import { useReduxSelector, useReduxDispatch } from '../../../redux/hooks';
import { TicketApi } from '../../../redux/api/TicketApi';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import { Card, CardHeader, CardBody, CardFooter } from '@progress/kendo-react-layout';
import './TicketDetailsPage.scss';
import CustomForm from './Sections/CustomForm';
import Actions from './Sections/Actions';
import { RevUserContext, RevUserContextType } from '../../../context/UserContext';
import AddAction from './Sections/AddAction';
import { RightBar } from '../../RightBar/RightBar';
import Dockbar from './Sections/Dock/Dockbar';
import AISummary from './Sections/AISummary';
import { setActionExpanded } from '../../../slices/TicketDetailUISlice';
import { setDetails } from '../../../slices/TicketDetailSlice';
import ActionToolbar from './Sections/ActionToolbar';
import EmailAction from './Sections/Tools/Email/EmailAction';
import TicketHeader from './Sections/TicketHeader';
import { ITicketProps } from '../../../types/Ticket/ITicket';
import '../../../styles/themes.scss';
import createTheme from '../../../styles/themes';
import Sidebar from './Sections/Sidebar';
import TicketStoryLine2 from './Sections/Tools/Storyline/TicketStoryLine2';
import ActionExpander from './Sections/ActionExpander';
import { Spinny } from '../../Nav/Spinny';
import ErrorCard from '../../Common/ErrorCard';
import { CloseDialogProvider } from './Sections/Tools/Close/CloseDialogContext';
import ActionTabs from './Sections/ActionTabs';
import TicketRoutes from '../TicketRoute';
import Progress from '../../Gameplan/Progress';
import { isMobile } from 'react-device-detect';
import GameplanDetails from '../../Gameplan/GameplanDetails';
import AddEvent from './Sections/Tools/Event/AddEvent';
import LiveTicketView from './Sections/LiveView';
import UserInfo from './Sections/UserInfo';
import Ownership from './Sections/Ownership';
import Attachments from './Sections/Attachments';
import Timeline from './Sections/Timeline';
import TicketType from './Sections/Type';
import Allocation from '../../Gameplan/Allocation';
import LegacyBanner from './Sections/LegacyBanner';

const TicketDetailsPage = () => {
    const navigate = useNavigate();
    const { profile, setHeaderText } = React.useContext<RevUserContextType>(RevUserContext)
    const { ticketId } = useParams()
    const { data: details, isFetching, error } = TicketApi.useGetTicketDetailsQuery({ ticketId: Number(ticketId) });
    const dispatch = useReduxDispatch();
    const viewName = useReduxSelector(state => state.ticketDetailsUI.viewName);
    const [markAsRead] = TicketApi.useReadTicketMutation();

    // useEffect(() => {
    //     setHeaderText(`${profile?.Phrases.CallPhrase} details`);
    // }, [details]);

    if (isFetching === false && error && error['status'] === 404) {
        navigate('/ticket/not-found');
    }


    React.useEffect(() => {
        if (details) {
            console.log('TicketDetailsPage mounted', ticketId);
            dispatch(setDetails(details));
            markTicketAsRead();
        }

    }, [details]);


    const markTicketAsRead = () => {
        markAsRead({ ticketId: Number(ticketId) }).unwrap();
    }

    //if (!details) return <Spinny /


    // return <><Dockbar />
    //     <Actions {...details} /></>
    //Onlt show loading on initial fetch when details is null OR when changing tickets and the Ticket IDs don't match
    const props = {
        ...details, ID: parseInt(ticketId),
        Is_Loading: details === null ? isFetching : (isFetching && ticketId !== details?.ID.toString())
    } as ITicketProps

    const pageThemeCSS = createTheme(profile.Body_Hex_Color, profile?.Body_Style, profile?.Body_Image_Url);


    if (error && error['originalStatus'] === 500) {
        console.log('ERROR', error);
        return <ErrorCard message={`Unexpected error while fetching details for ticket #${ticketId}. Code 500 API error: `} details={error['data']} />
    }

    const visibleSection = {
        'summary': <AISummary {...props} />,
        'timeline': <TicketStoryLine2 {...props} />,
        'gameplan': <GameplanDetails {...props} />,
        'details': <UserInfo {...props} />,
        'ownership': <><Ownership {...props} /><Allocation {...props} editMode={false} /></>,
        'attach': <Attachments {...props} editMode={false} />,
        'status': <><Timeline {...props} /><TicketType {...props} /></>,
    } as Record<string, React.ReactNode>;



    //First load
    if (!details) return <Spinny />
    return (<>
        <CloseDialogProvider>
            <TicketRoutes />
            <RightBar>
                <div className={`ticket-details-page overflow-auto fullscreen`} style={pageThemeCSS}>
                    <div className="container">
                        {process.env.NODE_ENV === 'development' && <LegacyBanner {...props} />}
                        <Outlet></Outlet>
                        <div className="row">
                            <Sidebar {...props} >
                                <div className="col content">
                                    <LiveTicketView />
                                    <Dockbar {...details} />
                                    <TicketHeader  {...props} />
                                    {!isMobile && <Progress {...props} />}
                                    {/* Custom Forms */}
                                    {!props.Is_Loading && <CustomForm {...details} />}
                                    <ActionTabs   {...props} />
                                    <div className={'actions-section' + (isMobile ? ' mobile-actions-section' : '')}>
                                        <Card>
                                            {(viewName === 'notes' || viewName == 'summary') && <CardHeader className='header'>
                                                <ActionToolbar {...props} />
                                            </CardHeader>}
                                            <CardBody>
                                                <ActionExpander />
                                                {viewName === 'notes' && <div className="action-form">
                                                    {/* <div className={showAction ? 'action-form-hidden action-form d-flex flex-column' : 'action-form-hidden d-flex flex-column'}> </div> */}
                                                    <div className={'collapse'} id='collapseAction'>
                                                        <AddAction showHeader={true} ticket={details} onsubmitted={() => dispatch(setActionExpanded(0))} onclosed={() => dispatch(setActionExpanded(0))} />
                                                    </div>
                                                    <div className={'collapse'} id='collapseEmail'>
                                                        <EmailAction onpopout={() => dispatch(setActionExpanded(0))} onsubmitted={() => dispatch(setActionExpanded(0))} onclosed={() => dispatch(setActionExpanded(0))} />
                                                    </div>
                                                    <div className={'collapse'} id='collapseEvent'>
                                                        <AddEvent showHeader={true} ticket={details} onsubmitted={() => dispatch(setActionExpanded(0))} onclosed={() => dispatch(setActionExpanded(0))} />
                                                    </div>
                                                    <Actions {...props} />
                                                </div>}
                                                {visibleSection[viewName]}
                                            </CardBody>
                                            <CardFooter></CardFooter>
                                        </Card>
                                    </div>
                                </div>
                            </Sidebar>
                        </div>
                    </div >
                </div>
            </RightBar >
        </CloseDialogProvider>
    </>
    );
};

export default TicketDetailsPage;